import { animate, state, style, transition, trigger } from '@angular/animations';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { createFormControl, EmailCollectorPackageInformation } from 'osn-shared';
import { emailRegexp } from 'projects/osn-shared/src/common';
import { BehaviorSubject, mapTo, Observable, Subject, switchMap, takeUntil, timer } from 'rxjs';
import { GoogleTagManagerEventAction } from '../../enums';
import { ClientConfigService } from '../../services';
import { AbstractEmailCollectorService } from './abstract-email-collector.service';
import { AdditionalInputType, EmailCollectorAdditionalControlDirective } from './email-collector-additional-control.directive';
import { EmailCollectorViewModel } from './email-collector.view-model';

@Component({
  selector: 'osn-shared-email-collector',
  templateUrl: './email-collector.component.html',
  styleUrls: ['./email-collector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('passwordInputAnimation', [
      state('hide', style({ marginTop: '-48px', visibility: 'hidden', opacity: 0 })),
      state('show', style({ marginTop: '-1px', visibility: 'visible', opacity: 1 })),
      transition('hide <=> show', animate('0.25s')),
    ]),
    trigger('borderRadiusAnimation', [
      state('rectangular', style({ borderBottomLeftRadius: '0px', borderBottomRightRadius: '0px' })),
      state('round', style({ borderBottomLeftRadius: '6px', borderBottomRightRadius: '6px' })),
      transition('rectangular <=> round', animate('0.25s')),
    ]),
  ],
})
export class EmailCollectorComponent implements OnDestroy, OnInit, AfterContentInit {
  @HostBinding('class.mlp-style') @Input() public mlpStyle = false;
  @Input() public inputPlaceholder: string;
  @Input() public passwordPlaceholder: string;
  @Input() public buttonText: string;
  @Input() public theme = 'dark';
  @Input() public showAgreementTextBelowCtaButton = false;
  @Input() public hideMobileProviderButton = false;
  @Input() public displayOnDialog = false;
  @Input() public contentTitle?: string;
  @Input() public userEmail?: string;
  @Input() public showTickMark = false;
  @Input() public emailCollectorPackageInformation: EmailCollectorPackageInformation;

  @Output() public submitted = new EventEmitter<void>();
  @Output() public loginError = new EventEmitter<void>();
  @Output() public startFreeTrialProcessStart = new EventEmitter<void>();

  @ViewChild('passwordInput') public passwordInput: ElementRef;
  @ViewChild('emailInput') public emailInput: ElementRef;
  @ContentChild('additionalInput') public additionalInput?: HTMLInputElement;
  @ContentChild(EmailCollectorAdditionalControlDirective) public additionalControl?: EmailCollectorAdditionalControlDirective;

  public emailFormControl = createFormControl('');
  public passwordFormControl = createFormControl('');

  public showLoadingSpinner$ = this.service.isLoading$;
  public isPasswordVisible = false;
  public currentLang = this.translateService.currentLang;
  public signUpWithMobileProviderLink = this.clientConfigService.signUpWithMobileProviderLink;
  public isFocusInProgress = false;
  public isPasswordTooShort = false;
  public isPasswordEmpty = false;
  public isEmailFormatInvalid = false;
  public isAdditionalInputInvalid = false;
  public readonly showMobileProviderButton$ = this.service.showMobileProviderButton$;
  public readonly freeTrialTextEnabled: boolean = false;

  public readonly termsAndConditionsLink =
    this.translateService.currentLang === 'en'
      ? this.clientConfigService.termsAndConditionsUrlEn
      : this.clientConfigService.termsAndConditionsUrlAr;
  public readonly privacyPolicyLink =
    this.translateService.currentLang === 'en' ? this.clientConfigService.privacyPolicyEn : this.clientConfigService.privacyPolicyAr;

  private readonly initialState = { showPasswordInput: false, emailErrorMessage: undefined, gatewayErrorMessage: undefined };

  private readonly _vm$ = new BehaviorSubject<EmailCollectorViewModel>({
    ...this.initialState,
  });
  public vm$: Observable<EmailCollectorViewModel> = this._vm$.asObservable();

  public inputHeight$: Observable<string>;
  private readonly onDestroy$ = new Subject<void>();

  constructor(
    private readonly service: AbstractEmailCollectorService,
    private readonly clientConfigService: ClientConfigService,
    private readonly translateService: TranslateService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.freeTrialTextEnabled = this.clientConfigService.freeTrialTextEnabled;
    this.emailFormControl.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe((value) => {
      this.passwordFormControl.reset();
      this._vm$.next({ ...this._vm$.value, emailErrorMessage: undefined, showPasswordInput: false, additionalInputError: undefined });
      this.isEmailFormatInvalid = !emailRegexp.test(value);
    });
    this.passwordFormControl.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe((value) => {
      this._vm$.next({ ...this._vm$.value, gatewayErrorMessage: undefined });
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      this.isPasswordTooShort = !!value?.length && value?.length < 6;
    });
  }

  public ngOnInit() {
    this.vm$
      .pipe(
        takeUntil(this.onDestroy$),
        switchMap((vm) => {
          this.isFocusInProgress = true;
          return timer(100).pipe(mapTo(vm));
        })
      )
      .subscribe((vm) => {
        if (!this.additionalControl) {
          if (vm.showPasswordInput) {
            this.passwordInput.nativeElement.focus();
          } else if (this.emailInput.nativeElement.value.length) {
            this.emailInput.nativeElement.focus();
          }
        }

        if (vm.additionalInputError) {
          this.additionalControl?.formControl.setErrors({ additionalInputError: vm.additionalInputError });
          this.additionalInput.focus();
          this.isAdditionalInputInvalid = true;
        }

        if (vm.showPasswordInput) {
          this.isPasswordEmpty = !this.passwordFormControl.value;
        } else {
          this.isPasswordEmpty = false;
        }

        this.cdr.markForCheck();
        this.isFocusInProgress = false;

        if (vm.emailErrorMessage || vm.gatewayErrorMessage) {
          this.loginError.emit();
        }
      });

    if (this.userEmail) {
      this.emailFormControl.setValue(this.userEmail);
      this.emailFormControl.disable();
    }
  }

  public ngAfterContentInit(): void {
    this.isAdditionalInputInvalid = !!this.additionalInput && !this.additionalControl?.formControl.value;
    this.isEmailFormatInvalid = !this.emailFormControl.value || !emailRegexp.test(this.emailFormControl.value);
    this.additionalControl?.formControl.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe((value) => {
      this._vm$.next({ ...this._vm$.value, additionalInputError: undefined });
      this.isAdditionalInputInvalid = this.additionalControl.validatorRegexp ? !this.additionalControl.validatorRegexp?.test(value) : false;
    });
  }

  public onEyeClick(): void {
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  public onStartFreeTrialSubmit(): void {
    if (!this._vm$.getValue().showPasswordInput) {
      const eventAction = this.displayOnDialog ? GoogleTagManagerEventAction.PopupEmailEntered : GoogleTagManagerEventAction.Signup;
      this.startFreeTrialProcessStart.emit();
      this.service.measureStartFreeTrialButtonClick(
        'Enter Email Address',
        this.buttonText,
        this.displayOnDialog,
        eventAction,
        this.contentTitle
      );
    } else {
      if (this.passwordFormControl.value?.length && this.displayOnDialog) {
        const eventAction = this.displayOnDialog ? GoogleTagManagerEventAction.PopupAccountCreated : GoogleTagManagerEventAction.Signup;
        this.service.measureStartFreeTrialButtonClick(
          'Account created',
          this.buttonText,
          this.displayOnDialog,
          eventAction,
          this.contentTitle
        );
      } else {
        const eventAction = this.displayOnDialog ? GoogleTagManagerEventAction.PopupEmailEntered : GoogleTagManagerEventAction.Signup;
        this.service.measureStartFreeTrialButtonClick(
          'Create Password',
          this.buttonText,
          this.displayOnDialog,
          eventAction,
          this.contentTitle
        );
      }
    }

    this.submitted.emit();
    this.service
      .startFreeTrial$(
        this._vm$.value,
        this.emailFormControl.value.toLowerCase().trim(),
        this.passwordFormControl.value,
        this.additionalControl?.additionalInputKey === AdditionalInputType.Voucher ? this.additionalControl.formControl.value : undefined,
        this.additionalControl?.additionalInputKey === AdditionalInputType.Bin ? this.additionalControl.formControl.value : undefined
      )
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((vm) => this._vm$.next({ ...vm }));
  }

  public ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  public onSignUpWithMobileProviderClick(): void {
    this.service.measureSignUpWithMobileProviderButtonClick('Sign up with mobile provider', this.displayOnDialog, this.contentTitle);
  }
}
